/**
 * Portfolio PDF Export utility.
 * Uses html2pdf.js (already in project dependencies) to generate a clean PDF from portfolio data.
 */

export const generatePortfolioPDF = async (profileData, options = {}) => {
  const { core, education = [], experience = [], projects = [], skills = [], languages = [], awards = [] } = profileData;
  const { themeColor = '#3b82f6' } = options;

  // Build clean HTML for PDF
  const html = `
    <div style="font-family: 'Helvetica Neue', Arial, sans-serif; color: #1e293b; max-width: 700px; margin: 0 auto; padding: 40px;">
      
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid ${themeColor}; padding-bottom: 20px;">
        <h1 style="font-size: 28px; font-weight: 800; margin: 0 0 8px 0; color: #0f172a;">${core.name || ''}</h1>
        <div style="font-size: 12px; color: #64748b; display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
          ${core.email ? `<span>✉ ${core.email}</span>` : ''}
          ${core.phone ? `<span>☎ ${core.phone}</span>` : ''}
          ${core.city ? `<span>📍 ${core.city}${core.country ? ', ' + core.country : ''}</span>` : ''}
        </div>
      </div>

      <!-- Summary -->
      ${core.summary ? `
        <div style="margin-bottom: 24px;">
          <h2 style="font-size: 13px; text-transform: uppercase; letter-spacing: 2px; color: ${themeColor}; margin: 0 0 8px 0; font-weight: 800;">Professional Summary</h2>
          <p style="font-size: 13px; line-height: 1.7; color: #475569; margin: 0;">${core.summary}</p>
        </div>
      ` : ''}

      <!-- Experience -->
      ${experience.length > 0 ? `
        <div style="margin-bottom: 24px;">
          <h2 style="font-size: 13px; text-transform: uppercase; letter-spacing: 2px; color: ${themeColor}; margin: 0 0 12px 0; font-weight: 800; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">Work Experience</h2>
          ${experience.map(exp => `
            <div style="margin-bottom: 16px;">
              <div style="display: flex; justify-content: space-between; align-items: baseline;">
                <h3 style="font-size: 14px; font-weight: 700; margin: 0; color: #0f172a;">${exp.job_title || ''}</h3>
                <span style="font-size: 11px; color: #94a3b8; white-space: nowrap;">${exp.start_date || ''} – ${exp.end_date || 'Present'}</span>
              </div>
              <div style="font-size: 12px; color: ${themeColor}; font-weight: 600; margin: 2px 0 6px 0;">${exp.company || exp.organization || ''}</div>
              <p style="font-size: 12px; line-height: 1.6; color: #64748b; margin: 0;">${exp.description || ''}</p>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <!-- Projects -->
      ${projects.length > 0 ? `
        <div style="margin-bottom: 24px;">
          <h2 style="font-size: 13px; text-transform: uppercase; letter-spacing: 2px; color: ${themeColor}; margin: 0 0 12px 0; font-weight: 800; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">Projects</h2>
          ${projects.map(proj => `
            <div style="margin-bottom: 12px;">
              <h3 style="font-size: 13px; font-weight: 700; margin: 0 0 4px 0; color: #0f172a;">${proj.title || ''}</h3>
              <p style="font-size: 12px; line-height: 1.6; color: #64748b; margin: 0;">${proj.description || ''}</p>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <!-- Education -->
      ${education.length > 0 ? `
        <div style="margin-bottom: 24px;">
          <h2 style="font-size: 13px; text-transform: uppercase; letter-spacing: 2px; color: ${themeColor}; margin: 0 0 12px 0; font-weight: 800; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">Education</h2>
          ${education.map(edu => `
            <div style="margin-bottom: 10px;">
              <div style="font-size: 13px; font-weight: 700; color: #0f172a;">${edu.degree || ''}</div>
              <div style="font-size: 12px; color: #64748b;">${edu.school || edu.organization_name || ''} • ${edu.start_date || ''} – ${edu.end_date || 'Present'}</div>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <!-- Skills -->
      ${skills.length > 0 ? `
        <div style="margin-bottom: 24px;">
          <h2 style="font-size: 13px; text-transform: uppercase; letter-spacing: 2px; color: ${themeColor}; margin: 0 0 10px 0; font-weight: 800; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">Skills</h2>
          <div style="display: flex; flex-wrap: wrap; gap: 6px;">
            ${skills.map(sk => `
              <span style="padding: 4px 12px; background: #f1f5f9; border-radius: 20px; font-size: 11px; color: #475569; font-weight: 600;">${sk.name || sk}</span>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Footer -->
      <div style="text-align: center; font-size: 10px; color: #cbd5e1; margin-top: 30px; padding-top: 12px; border-top: 1px solid #e2e8f0;">
        Generated from CVLogs Portfolio • ${new Date().toLocaleDateString()}
      </div>
    </div>
  `;

  // Use html2pdf.js
  const { default: html2pdf } = await import('html2pdf.js');
  
  const container = document.createElement('div');
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    await html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `${(core.name || 'portfolio').replace(/\s+/g, '_')}_CV.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(container)
      .save();
  } finally {
    document.body.removeChild(container);
  }
};
